import type { WorkflowInputDefinition } from '@chatsdet/types';
import type { OrgCredential } from '../../../hooks/useOrgCredentials';
import type { SeedDataOutput } from '../../../hooks/useSeedWorkflowOutputs';
import type { WorkflowNode } from '../../../types/workflow';

function textOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;
}

export function deriveLoginNodeLabels({
  nodes,
  credentials,
  credentialPropertyMap,
  seedOutputsByWorkflowId,
  templateNameById,
  workflowInputs,
}: {
  nodes: WorkflowNode[];
  credentials: OrgCredential[];
  credentialPropertyMap: Map<string, string | null>;
  seedOutputsByWorkflowId?: Map<string, SeedDataOutput[]>;
  templateNameById?: Map<string, string>;
  workflowInputs?: WorkflowInputDefinition[];
}): WorkflowNode[] {
  if (nodes.length === 0) {
    return nodes;
  }

  const credentialNameMap = new Map<string, string>();
  for (const cred of credentials) {
    credentialNameMap.set(cred.id, cred.name);
  }

  return nodes.map((node) => {
    if (node.type !== 'login') {
      return node;
    }

    const data = node.data as Record<string, unknown> | undefined;
    const credentialSource = data?.credentialSource as string | undefined;

    // Template credential: label = template name
    if (credentialSource === 'from_template') {
      const templateId =
        typeof data?.credentialTemplateId === 'string'
          ? (data.credentialTemplateId as string)
          : null;
      if (templateId && templateNameById) {
        const templateName = templateNameById.get(templateId) ?? null;
        if (templateName) {
          return {
            ...node,
            data: {
              ...data,
              credentialName: templateName,
              credentialTemplateName: templateName,
            },
          };
        }
      }
      return node;
    }

    // Scenario credential input: label = the credential input's label (e.g. "Personal")
    if (credentialSource === 'from_input') {
      const inputName =
        typeof data?.credentialInputName === 'string' ? data.credentialInputName : null;
      if (inputName && workflowInputs) {
        const match = workflowInputs.find(
          (input) => input.kind === 'credential' && input.name === inputName
        );
        const label = match?.label || match?.name || inputName;
        return {
          ...node,
          data: { ...data, credentialName: label },
        };
      }
      return node;
    }

    // Seed credential: derive label from seed output name
    if (credentialSource === 'from_seed' && seedOutputsByWorkflowId) {
      const seedRef = data?.seedCredentialRef as
        | { seedWorkflowId?: string; dataKey?: string }
        | undefined;
      if (seedRef?.seedWorkflowId && seedRef?.dataKey) {
        const outputs = seedOutputsByWorkflowId.get(seedRef.seedWorkflowId);
        const matchingOutput = outputs?.find((o) => o.dataKey === seedRef.dataKey);
        if (matchingOutput) {
          const label = textOrNull(matchingOutput.name) ?? textOrNull(matchingOutput.dataKey);
          if (!label) return node;
          return {
            ...node,
            data: { ...data, credentialName: label },
          };
        }
      }
      return node;
    }

    // Static credential: derive from credential name + property
    const credentialId =
      typeof data?.credentialId === 'string'
        ? (data.credentialId as string)
        : null;

    if (!credentialId) {
      return node;
    }

    const propertyName = credentialPropertyMap.get(credentialId) ?? null;
    const credentialName = credentialNameMap.get(credentialId) ?? null;

    if (!propertyName && !credentialName) {
      return node;
    }

    return {
      ...node,
      data: {
        ...data,
        ...(propertyName ? { propertyName } : {}),
        ...(credentialName ? { credentialName } : {}),
      },
    };
  });
}
